import { createAsyncThunk } from '@reduxjs/toolkit';
import { UserRepository } from 'repositories/UserRepository';
import { IAuthState } from 'store/slices/authSlice';
import { IUserAccrualState } from 'store/slices/userAccrualSlice';
import { IAccrual, TAccrualsQuery } from 'types/accruals';
import { IApiResponseDetails } from 'types/api';

export const fetchAccrualsList = createAsyncThunk<
  { content: IAccrual[]; details: IApiResponseDetails },
  void,
  { state: { auth: IAuthState; userAccruals: IUserAccrualState } }
>('userAccruals/fetchAccrualsList', async (_, { getState }) => {
  const {
    auth: { token },
    userAccruals: { currentPage, itemsPerPage, search, sortBy, sortDir, fromDate, toDate },
  } = getState();

  if (!token) throw Error('no token');

  const params: TAccrualsQuery = { page: String(currentPage), size: String(itemsPerPage) };
  if (search) params.filter = search;
  if (sortBy) {
    params.sort = sortDir ? `accruals.${sortBy},${sortDir.toLowerCase()}` : sortBy;
  }
  if (fromDate) params.from = fromDate;
  if (toDate) params.to = toDate;
  const accrualsListResponse = await UserRepository.getAccruals(token, params);

  if (accrualsListResponse.status === 'failed') throw Error(accrualsListResponse.error);

  return accrualsListResponse.data;
});

export const fetchAccrualsListGraph = createAsyncThunk<
    { content: IAccrual[]; details: IApiResponseDetails },
    void,
    { state: { auth: IAuthState; userAccruals: IUserAccrualState } }
>('userAccruals/fetchAccrualsListGraph', async (_, { getState }) => {
  const {
    auth: { token },
    userAccruals: { currentPage, itemsPerPage, search, sortBy, sortDir, fromDate, toDate },
  } = getState();

  if (!token) throw Error('no token');

  const params: TAccrualsQuery = { page: String(currentPage), size: String(itemsPerPage) };
  if (search) params.filter = search;
  if (sortBy) {
    params.sort = sortDir ? `accruals.${sortBy},${sortDir.toLowerCase()}` : sortBy;
  }
  const now = new Date();
  const defaultTo = now.toISOString();
  const back = new Date();
  back.setDate(now.getDate() - 12);
  const defaultFrom = back.toISOString();
  params.from = defaultFrom;
  params.to = defaultTo;

  const accrualsListResponse = await UserRepository.getAccruals(token, params);

  if (accrualsListResponse.status === 'failed') throw Error(accrualsListResponse.error);

  return accrualsListResponse.data;
});

export const fetchExportAccruals = createAsyncThunk<
  { exportData: string },
  void,
  { state: { auth: IAuthState } }
>('userAccruals/fetchExportAccruals', async (_, { getState }) => {
  const {
    auth: { token },
  } = getState();

  if (!token) throw Error('no token');

  const exportAccrualsResponse = await UserRepository.getExportAccruals(token);

  if (exportAccrualsResponse.status === 'failed') throw Error(exportAccrualsResponse.error);

  return {
    exportData: exportAccrualsResponse.data,
  };
});
